import type { Client } from '@frontend/api-types';
import { convertObjectKeysCase } from '@frontend/duck-tape';
import { useTrackingSlice } from '@frontend/redux';
import { getAttributionCookies, type SegmentClient } from '@frontend/web-utils/segment';
import { useCallback, useEffect, useRef } from 'react';

/**
 * This hook should never be used directly. It should only ever be used by the `useAnalytics` hook from the `@frontend/react` package.
 */
export const useSegment = ({ analytics, env }: { analytics: SegmentClient; env: EnvType }) => {
  const { fbclid, gclid } = useTrackingSlice();
  const clickParamsRef = useRef({ fbclid, gclid });

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    clickParamsRef.current = { fbclid, gclid };
  }, [fbclid, gclid]);

  const track = useCallback(
    (object: string, action: string, data: Record<string, unknown> = {}, client?: Client) => {
      const { fbclid: currentFbclid, gclid: currentGclid } = clickParamsRef.current;
      const clientData = client ? { clientId: client.id } : {};
      const attributionCookies = getAttributionCookies();
      const snakeCasedData = {
        ...convertObjectKeysCase(clientData, 'snakeCase'),
        ...convertObjectKeysCase(data, 'snakeCase'),
        ...attributionCookies,
        ...(currentGclid && { gclid: currentGclid }),
        ...(currentFbclid && { fbclid: currentFbclid }),
      };
      const label = `${object} ${action}`;
      if (env === 'local') {
        // eslint-disable-next-line no-console
        console.info('Mock Segment track:', {
          action,
          data: snakeCasedData,
          label,
        });
        return;
      }
      analytics.track(label, snakeCasedData);
      return;
    },
    [analytics, env],
  );

  const trackRouteChange = useCallback(
    (params: {
      category?: string;
      name?: string;
      properties?: {
        path?: string;
        referrer?: string;
        search?: Record<string, string>;
        searchStr?: string;
        title?: string;
        url?: string;
      };
    }) => {
      const { fbclid: currentFbclid, gclid: currentGclid } = clickParamsRef.current;
      const attributionCookies = getAttributionCookies();
      const propertiesWithAttribution = {
        ...params.properties,
        ...attributionCookies,
        ...(currentGclid && { gclid: currentGclid }),
        ...(currentFbclid && { fbclid: currentFbclid }),
      };
      if (env === 'local') {
        // eslint-disable-next-line no-console
        console.info('Mock Segment page track:', { ...params, properties: propertiesWithAttribution });
        return;
      }
      analytics.page(params.category, params.name, propertiesWithAttribution);
      return;
    },
    [analytics, env],
  );

  return { track, trackRouteChange };
};
